import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import api from '../utils/api';

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [payment, setPayment] = useState(null);

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    const transactionId = searchParams.get('transaction_id') || searchParams.get('txn');
    
    if (!transactionId) {
      setStatus('error');
      return;
    }

    try {
      // Wait a bit for webhook to process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const response = await api.get(`/payments/verify/${transactionId}`);
      setPayment(response.data);
      
      if (response.data.status === 'completed') {
        setStatus('success');
      } else if (response.data.status === 'failed') {
        setStatus('failed');
      } else {
        setStatus('pending');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="payment-success-page">
      <Card className="max-w-md w-full p-8 text-center rounded-3xl">
        {status === 'verifying' && (
          <div className="space-y-4" data-testid="payment-verifying">
            <Loader2 className="w-16 h-16 text-electric-coral mx-auto animate-spin" />
            <h2 className="text-2xl font-heading font-bold">Verifying Payment</h2>
            <p className="text-slate-600">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-4" data-testid="payment-success">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-12 h-12 text-green-500" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-green-600">Payment Successful!</h2>
            <p className="text-slate-600">
              {payment?.payment_type === 'subscription'
                ? 'Your subscription is now active. Enjoy premium features!'
                : 'Your purchase was successful!'}
            </p>
            <div className="pt-4">
              <p className="text-sm text-slate-500">Amount: {payment?.amount} ETB</p>
              <p className="text-sm text-slate-500">Transaction ID: {payment?.transaction_id}</p>
            </div>
            <Button
              onClick={() => navigate('/discover')}
              className="w-full rounded-full bg-gradient-to-r from-electric-coral to-deep-violet mt-6"
              data-testid="continue-button"
            >
              Continue
            </Button>
          </div>
        )}

        {status === 'pending' && (
          <div className="space-y-4" data-testid="payment-pending">
            <Loader2 className="w-16 h-16 text-yellow-500 mx-auto animate-spin" />
            <h2 className="text-2xl font-heading font-bold text-yellow-600">Payment Processing</h2>
            <p className="text-slate-600">
              Your payment is being processed. You'll receive a confirmation shortly.
            </p>
            <Button
              onClick={() => navigate('/discover')}
              variant="outline"
              className="w-full rounded-full mt-6"
            >
              Continue
            </Button>
          </div>
        )}

        {(status === 'failed' || status === 'error') && (
          <div className="space-y-4" data-testid="payment-failed">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-red-600">Payment Failed</h2>
            <p className="text-slate-600">
              {status === 'error'
                ? 'Unable to verify your payment. Please contact support if you were charged.'
                : 'Your payment could not be completed. Please try again.'}
            </p>
            <div className="flex gap-4 mt-6">
              <Button
                onClick={() => navigate('/subscription')}
                className="flex-1 rounded-full"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/discover')}
                variant="outline"
                className="flex-1 rounded-full"
              >
                Go Back
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};